/**
 * THE RUN's visuals: claims as amber octahedra hovering and turning over the street (a dropped
 * one burns magenta), safe zones as a cyan ring on the ground with a slow column of light and a
 * label. Render only; the sim owns the truth.
 */
import * as THREE from "three";
import { markShared, release } from "./dispose";
import { bindPlate, PALETTE } from "./city";
import { screens } from "./screens";

export interface RunClaimView {
  id: number;
  x: number;
  y: number;
  z: number;
  value: number;
  dropped: boolean;
}
export interface RunZoneView {
  label: string;
  x: number;
  z: number;
  radius: number;
}

export class RunFx {
  private claims = new Map<number, { mesh: THREE.Mesh; mat: THREE.MeshBasicMaterial; light: THREE.PointLight; value: number; dropped: boolean }>();
  private zones: { group: THREE.Group; ring: THREE.Mesh; column: THREE.Mesh; colMat: THREE.MeshBasicMaterial }[] = [];
  private zoneKey = "";
  private clock = 0;
  /** one octahedron for every claim: shared, so a claim that is picked up must not dispose it */
  private geo = markShared(new THREE.OctahedronGeometry(0.28, 0));

  constructor(private scene: THREE.Scene) {}

  private labelSprite(text: string): THREE.Sprite {
    const c = document.createElement("canvas");
    c.width = 256;
    c.height = 48;
    const g = c.getContext("2d")!;
    g.font = "bold 28px Courier New, monospace";
    g.textAlign = "center";
    g.textBaseline = "middle";
    g.fillStyle = "#ffffff";
    g.fillText(text, 128, 26);
    const tex = new THREE.CanvasTexture(c);
    tex.magFilter = THREE.NearestFilter;
    const s = new THREE.Sprite(new THREE.SpriteMaterial({ map: tex, transparent: true, depthWrite: false, color: PALETTE.cyan }));
    s.scale.set(5, 0.95, 1);
    return s;
  }

  syncZones(views: readonly RunZoneView[]): void {
    const key = views.map((z) => `${z.label}:${z.x}:${z.z}:${z.radius}`).join("|");
    if (key === this.zoneKey) return;
    this.zoneKey = key;
    for (const z of this.zones) release(z.group);
    this.zones = [];
    for (const z of views) {
      const group = new THREE.Group();
      const ringMat = new THREE.MeshBasicMaterial({ color: PALETTE.cyan, transparent: true, opacity: 0.55, side: THREE.DoubleSide, depthWrite: false });
      bindPlate(ringMat, "tex_lamp");
      const ring = new THREE.Mesh(new THREE.RingGeometry(z.radius - 0.35, z.radius, 48), ringMat);
      ring.rotation.x = -Math.PI / 2;
      ring.position.y = 0.03;
      group.add(ring);
      const colMat = new THREE.MeshBasicMaterial({ color: PALETTE.cyan, transparent: true, opacity: 0.06, depthWrite: false, side: THREE.DoubleSide });
      bindPlate(colMat, "tex_lamp");
      const column = new THREE.Mesh(new THREE.CylinderGeometry(z.radius, z.radius, 9, 32, 1, true), colMat);
      column.position.y = 4.5;
      group.add(column);
      const label = this.labelSprite(`SAFE · ${z.label}`);
      label.position.y = 3.2;
      group.add(label);
      // the market kiosk: a counter, a screen lit amber, a MARKET sign — the panel's market opens from here
      const kiosk = new THREE.Group();
      const kioskMat = new THREE.MeshStandardMaterial({ color: 0x0c1016, roughness: 0.6, metalness: 0.4 });
      bindPlate(kioskMat, "tex_metal");
      kiosk.add(new THREE.Mesh(new THREE.BoxGeometry(1.8, 1.1, 0.7), kioskMat));
      const screenMat = new THREE.MeshBasicMaterial({ color: PALETTE.amber });
      bindPlate(screenMat, "tex_kiosk_crt");
      // the one screen a player walks up to and reads, so it is worth a decoder; if the pool is
      // already full the kiosk keeps its plate and nothing about the safe zone changes (Stage 633)
      screens.attach(screenMat, "kiosk", 0);
      const screen = new THREE.Mesh(new THREE.PlaneGeometry(1.4, 0.7), screenMat);
      screen.position.set(0, 1.25, 0.36);
      kiosk.add(screen);
      const sign = this.labelSprite("MARKET");
      sign.scale.set(2.6, 0.5, 1);
      sign.position.y = 2.0;
      (sign.material as THREE.SpriteMaterial).color.set(PALETTE.amber);
      kiosk.add(sign);
      const glow = new THREE.PointLight(PALETTE.amber, 0.8, 6, 2);
      glow.position.set(0, 1.4, 0.8);
      kiosk.add(glow);
      kiosk.position.set(z.radius * 0.55, 0.55, -z.radius * 0.55);
      kiosk.rotation.y = -Math.PI / 4;
      group.add(kiosk);
      group.position.set(z.x, 0, z.z);
      this.scene.add(group);
      this.zones.push({ group, ring, column, colMat });
    }
  }

  syncClaims(views: readonly RunClaimView[]): void {
    const seen = new Set<number>();
    for (const v of views) {
      seen.add(v.id);
      let e = this.claims.get(v.id);
      if (!e) {
        const mat = new THREE.MeshBasicMaterial({ color: v.dropped ? PALETTE.magenta : PALETTE.amber });
        bindPlate(mat, "tex_kiosk_crt");
        const mesh = new THREE.Mesh(this.geo, mat);
        const light = new THREE.PointLight(v.dropped ? PALETTE.magenta : PALETTE.amber, 0.9, 5, 2);
        mesh.add(light);
        this.scene.add(mesh);
        e = { mesh, mat, light, value: v.value, dropped: v.dropped };
        this.claims.set(v.id, e);
      }
      e.value = v.value;
      const scale = 0.8 + Math.min(4, v.value) * 0.18;
      e.mesh.scale.setScalar(scale);
      e.mesh.position.set(v.x, v.y + 1.0, v.z);
    }
    for (const [id, e] of this.claims) {
      if (seen.has(id)) continue;
      release(e.mesh);
      this.claims.delete(id);
    }
  }

  update(dt: number): void {
    this.clock += dt;
    for (const e of this.claims.values()) {
      e.mesh.rotation.y += dt * 1.6;
      e.mesh.position.y += Math.sin(this.clock * 2.2 + e.mesh.position.x) * dt * 0.12;
      e.light.intensity = 0.7 + 0.3 * Math.sin(this.clock * 3 + e.mesh.position.z);
    }
    for (const z of this.zones) z.colMat.opacity = 0.05 + 0.03 * Math.sin(this.clock * 0.8);
  }

  get count(): number {
    return this.claims.size;
  }
}
